import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ImagePlus,
  Images,
  Video,
  Trash2,
  Plus,
  RefreshCw,
  Search,
  X,
  Upload,
  CalendarDays,
  Eye,
  AlertCircle,
  CheckCircle2,
  LoaderCircle,
  FolderOpen,
  Play,
} from "lucide-react";

const API_BASE_URL = "https://pmswebbackend.pixelmindsolutions.com";
const GALLERY_API = `${API_BASE_URL}/api/galleries`;

const GALLERY_TYPES = [
  { value: "festival", label: "Festival" },
  { value: "celebration", label: "Celebration" },
  { value: "office", label: "Office" },
];

const ACCEPTED_MEDIA =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,video/x-m4v";

const getMediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}/${url.replace(/^\/+/, "")}`;
};

const getGalleryMedia = (gallery) => {
  // Supports the updated API ("media") and old records ("images").
  if (Array.isArray(gallery?.media)) return gallery.media;
  if (Array.isArray(gallery?.images)) {
    return gallery.images.map((item) => ({
      ...item,
      mediaType: item.mediaType || "image",
    }));
  }
  return [];
};

const isVideo = (item) =>
  item?.mediaType === "video" ||
  item?.mimetype?.startsWith("video/") ||
  /\.(mp4|webm|mov|m4v)$/i.test(item?.originalName || item?.filename || item?.url || "");

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

export default function GalleryManagement() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingMediaId, setDeletingMediaId] = useState(null);

  const [galleryType, setGalleryType] = useState("festival");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [notice, setNotice] = useState({ type: "", message: "" });

  const [activeGallery, setActiveGallery] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  const createInputRef = useRef(null);
  const addInputRef = useRef(null);

  const previewUrls = useMemo(
    () =>
      selectedFiles.map((file) => ({
        name: file.name,
        type: file.type.startsWith("video/") ? "video" : "image",
        url: URL.createObjectURL(file),
      })),
    [selectedFiles]
  );

  useEffect(() => {
    return () => previewUrls.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  const showNotice = (type, message) => setNotice({ type, message });

  const fetchGalleries = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await axios.get(GALLERY_API);
      const result = response.data;
      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];
      setGalleries(list);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, []);

  const filteredGalleries = useMemo(() => {
    const query = search.trim().toLowerCase();

    return galleries.filter((gallery) => {
      const matchesType =
        filterType === "all" || gallery.galleryType === filterType;
      const media = getGalleryMedia(gallery);
      const matchesSearch =
        !query ||
        gallery.galleryType?.toLowerCase().includes(query) ||
        media.some((item) =>
          (item.originalName || item.filename || "").toLowerCase().includes(query)
        );

      return matchesType && matchesSearch;
    });
  }, [galleries, search, filterType]);

  const totalMedia = galleries.reduce(
    (total, gallery) => total + getGalleryMedia(gallery).length,
    0
  );
  const totalImages = galleries.reduce(
    (total, gallery) =>
      total + getGalleryMedia(gallery).filter((item) => !isVideo(item)).length,
    0
  );
  const totalVideos = totalMedia - totalImages;

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles((previous) => [...previous, ...files]);
    event.target.value = "";
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((previous) => previous.filter((_, i) => i !== index));
  };

  const uploadFormData = (files, galleryTypeValue) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("media", file));
    if (galleryTypeValue) formData.append("galleryType", galleryTypeValue);
    return formData;
  };

  const handleCreateGallery = async (event) => {
    event.preventDefault();

    if (!selectedFiles.length) {
      showNotice("error", "Please select at least one image or video.");
      return;
    }

    const existingGallery = galleries.find(
      (gallery) => gallery.galleryType === galleryType
    );

    if (existingGallery) {
      showNotice(
        "error",
        `A ${galleryType} gallery already exists. Add media to the existing gallery instead.`
      );
      return;
    }

    setSaving(true);
    setNotice({ type: "", message: "" });

    try {
      await axios.post(
        GALLERY_API,
        uploadFormData(selectedFiles, galleryType)
      );

      setSelectedFiles([]);
      setGalleryType("festival");
      if (createInputRef.current) createInputRef.current.value = "";
      showNotice("success", "Gallery created successfully.");
      await fetchGalleries(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAddMedia = async (gallery, files) => {
    if (!files.length) return;

    setUploadingId(gallery._id);
    setNotice({ type: "", message: "" });

    try {
      await axios.post(
        `${GALLERY_API}/${gallery._id}/media`,
        uploadFormData(files)
      );
      showNotice("success", "Images and videos added successfully.");
      await fetchGalleries(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setUploadingId(null);
      if (addInputRef.current) addInputRef.current.value = "";
    }
  };

  const handleDeleteMedia = async (gallery, item) => {
    const video = isVideo(item);
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${video ? "video" : "image"}?`
    );
    if (!confirmed) return;

    setDeletingMediaId(item._id);
    setNotice({ type: "", message: "" });

    try {
      await axios.delete(
        `${GALLERY_API}/${gallery._id}/media/${item._id}`
      );

      showNotice("success", `${video ? "Video" : "Image"} deleted successfully.`);
      if (previewMedia?._id === item._id) setPreviewMedia(null);
      await fetchGalleries(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setDeletingMediaId(null);
    }
  };

  const handleDeleteGallery = async (gallery) => {
    const confirmed = window.confirm(
      `Delete the entire "${gallery.galleryType}" gallery and all its images/videos? This action cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(gallery._id);
    setNotice({ type: "", message: "" });

    try {
      await axios.delete(`${GALLERY_API}/${gallery._id}`);
      if (activeGallery?._id === gallery._id) setActiveGallery(null);
      showNotice("success", "Gallery deleted successfully.");
      await fetchGalleries(true);
    } catch (error) {
      showNotice("error", getErrorMessage(error));
    } finally {
      setDeletingId(null);
    }
  };

  const getTypeLabel = (type) =>
    GALLERY_TYPES.find((item) => item.value === type)?.label || type;

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-800 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-orange-600">
              <Images size={17} />
              Content Management
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Gallery Management
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Create galleries and manage photos and videos in one place.
            </p>
          </div>

          <button
            type="button"
            onClick={() => fetchGalleries(true)}
            disabled={refreshing}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold shadow-sm transition hover:bg-slate-100 disabled:opacity-60 sm:self-auto"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {notice.message && (
          <div
            role="status"
            className={`flex items-start gap-3 rounded-xl border p-4 ${
              notice.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {notice.type === "success" ? (
              <CheckCircle2 className="mt-0.5 shrink-0" size={19} />
            ) : (
              <AlertCircle className="mt-0.5 shrink-0" size={19} />
            )}
            <p className="flex-1 text-sm">{notice.message}</p>
            <button
              type="button"
              onClick={() => setNotice({ type: "", message: "" })}
              aria-label="Dismiss notification"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={<FolderOpen size={21} />} label="Total Galleries" value={galleries.length} />
          <StatCard icon={<Images size={21} />} label="Total Images" value={totalImages} />
          <StatCard icon={<Video size={21} />} label="Total Videos" value={totalVideos} />
          <StatCard icon={<ImagePlus size={21} />} label="Total Media" value={totalMedia} />
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
            <h2 className="flex items-center gap-2 text-lg font-bold">
              <Plus className="text-orange-600" size={20} />
              Create New Gallery
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Choose a category and upload multiple images and/or videos.
            </p>
          </div>

          <form onSubmit={handleCreateGallery} className="space-y-5 p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label htmlFor="galleryType" className="mb-2 block text-sm font-semibold text-slate-700">
                  Gallery Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="galleryType"
                  value={galleryType}
                  onChange={(event) => setGalleryType(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                >
                  {GALLERY_TYPES.map((type) => {
                    const exists = galleries.some(
                      (gallery) => gallery.galleryType === type.value
                    );
                    return (
                      <option key={type.value} value={type.value} disabled={exists}>
                        {type.label}{exists ? " (Already created)" : ""}
                      </option>
                    );
                  })}
                </select>
                <p className="mt-2 text-xs text-slate-500">
                  Each category can have one gallery. Add more media to existing galleries below.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Upload Images and Videos <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => createInputRef.current?.click()}
                  className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl border border-dashed border-orange-300 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                >
                  <Upload size={18} />
                  Choose Images / Videos
                </button>
                <input
                  ref={createInputRef}
                  type="file"
                  accept={ACCEPTED_MEDIA}
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <p className="mt-2 text-xs text-slate-500">
                  JPG, PNG, WEBP, GIF, MP4, WEBM, MOV, M4V. Up to 50 files per request.
                </p>
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold">
                    Selected Media ({selectedFiles.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedFiles([])}
                    className="text-xs font-semibold text-red-600 hover:underline"
                  >
                    Clear all
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {previewUrls.map((preview, index) => (
                    <div
                      key={`${preview.name}-${index}`}
                      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white"
                    >
                      {preview.type === "video" ? (
                        <div className="relative">
                          <video src={preview.url} className="h-28 w-full object-cover" muted />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
                            <Play size={28} fill="currentColor" />
                          </span>
                        </div>
                      ) : (
                        <img src={preview.url} alt={preview.name} className="h-28 w-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(index)}
                        aria-label={`Remove ${preview.name}`}
                        className="absolute right-2 top-2 rounded-lg bg-white/95 p-1.5 text-red-600 shadow transition hover:bg-red-50"
                      >
                        <X size={15} />
                      </button>
                      <div className="flex items-center gap-1 px-2 py-2 text-xs text-slate-600">
                        {preview.type === "video" ? <Video size={13} className="shrink-0" /> : <ImagePlus size={13} className="shrink-0" />}
                        <p className="truncate">{preview.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving || selectedFiles.length === 0}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {saving ? (
                  <>
                    <LoaderCircle size={18} className="animate-spin" />
                    Creating Gallery...
                  </>
                ) : (
                  <>
                    <ImagePlus size={18} />
                    Create Gallery
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Existing Galleries</h2>
              <p className="mt-1 text-sm text-slate-500">
                Manage your gallery collections, photos, and videos.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative">
                <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search galleries or media..."
                  className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 sm:w-60"
                />
              </div>
              <select
                value={filterType}
                onChange={(event) => setFilterType(event.target.value)}
                aria-label="Filter by gallery type"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500"
              >
                <option value="all">All Categories</option>
                {GALLERY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-slate-500">
              <LoaderCircle size={32} className="animate-spin text-orange-600" />
              <p className="text-sm">Loading galleries...</p>
            </div>
          ) : filteredGalleries.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center">
              <div className="mb-4 rounded-2xl bg-orange-50 p-4 text-orange-600"><Images size={32} /></div>
              <h3 className="font-bold text-slate-800">No galleries found</h3>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                {galleries.length
                  ? "Try changing your search or category filter."
                  : "Create your first gallery using the form above."}
              </p>
            </div>
          ) : (
            <div className="space-y-5 p-4 sm:p-6">
              {filteredGalleries.map((gallery) => {
                const mediaItems = getGalleryMedia(gallery);
                const imageCount = mediaItems.filter((item) => !isVideo(item)).length;
                const videoCount = mediaItems.length - imageCount;

                return (
                  <article key={gallery._id} className="overflow-hidden rounded-2xl border border-slate-200">
                    <div className="flex flex-col justify-between gap-4 bg-slate-50 p-4 sm:flex-row sm:items-center sm:p-5">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-orange-100 p-3 text-orange-700"><Images size={21} /></div>
                        <div>
                          <h3 className="text-base font-bold capitalize text-slate-900">
                            {getTypeLabel(gallery.galleryType)} Gallery
                          </h3>
                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1"><ImagePlus size={13} />{imageCount} images</span>
                            <span className="inline-flex items-center gap-1"><Video size={13} />{videoCount} videos</span>
                            <span className="inline-flex items-center gap-1"><CalendarDays size={13} />Created {formatDate(gallery.createdAt)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveGallery(gallery);
                            window.setTimeout(() => addInputRef.current?.click(), 0);
                          }}
                          disabled={uploadingId === gallery._id}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold transition hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
                        >
                          {uploadingId === gallery._id ? <LoaderCircle size={15} className="animate-spin" /> : <Plus size={15} />}
                          Add Media
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteGallery(gallery)}
                          disabled={deletingId === gallery._id}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === gallery._id ? <LoaderCircle size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          Delete Gallery
                        </button>
                      </div>
                    </div>

                    {activeGallery?._id === gallery._id && (
                      <div className="border-t border-slate-100 bg-orange-50 px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-sm text-orange-800">Select images and/or videos to add to this gallery.</p>
                          <button
                            type="button"
                            onClick={() => setActiveGallery(null)}
                            className="text-xs font-semibold text-slate-600 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                        <input
                          ref={addInputRef}
                          type="file"
                          accept={ACCEPTED_MEDIA}
                          multiple
                          className="mt-3 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-orange-700"
                          onChange={(event) => {
                            const files = Array.from(event.target.files || []);
                            event.target.value = "";
                            if (files.length) {
                              handleAddMedia(gallery, files);
                              setActiveGallery(null);
                            }
                          }}
                        />
                      </div>
                    )}

                    {mediaItems.length ? (
                      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 sm:p-5 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                        {mediaItems.map((item) => {
                          const video = isVideo(item);
                          return (
                            <div key={item._id || item.filename} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white">
                              <button
                                type="button"
                                onClick={() => setPreviewMedia({ ...item, galleryType: gallery.galleryType })}
                                title={`Preview ${video ? "video" : "image"}`}
                                className="relative block w-full text-left"
                              >
                                {video ? (
                                  <div className="relative">
                                    <video
                                      src={getMediaUrl(item.url)}
                                      className="h-32 w-full bg-black object-cover"
                                      preload="metadata"
                                      muted
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
                                      <Play size={30} fill="currentColor" />
                                    </span>
                                    <span className="absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-[10px] font-semibold text-white">VIDEO</span>
                                  </div>
                                ) : (
                                  <img
                                    src={getMediaUrl(item.url)}
                                    alt={item.originalName || "Gallery image"}
                                    loading="lazy"
                                    className="h-32 w-full object-cover transition duration-300 group-hover:scale-105"
                                    onError={(event) => { event.currentTarget.style.opacity = "0.3"; }}
                                  />
                                )}
                              </button>
                              <div className="flex items-center justify-between gap-2 px-2 py-2">
                                <div className="flex min-w-0 items-center gap-1">
                                  {video ? <Video size={13} className="shrink-0 text-orange-600" /> : <ImagePlus size={13} className="shrink-0 text-orange-600" />}
                                  <span className="truncate text-xs text-slate-600">{item.originalName || item.filename}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteMedia(gallery, item)}
                                  disabled={deletingMediaId === item._id}
                                  title={`Delete ${video ? "video" : "image"}`}
                                  aria-label={`Delete ${video ? "video" : "image"}`}
                                  className="shrink-0 rounded-md p-1.5 text-red-500 transition hover:bg-red-50 disabled:opacity-50"
                                >
                                  {deletingMediaId === item._id
                                    ? <LoaderCircle size={15} className="animate-spin" />
                                    : <Trash2 size={15} />}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-sm text-slate-500">
                        No media in this gallery yet. Click Add Media to upload images or videos.
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {previewMedia && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              aria-label="Close preview"
              className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-2 text-slate-800 shadow-lg"
            >
              <X size={20} />
            </button>
            {isVideo(previewMedia) ? (
              <video
                src={getMediaUrl(previewMedia.url)}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-xl bg-black"
              />
            ) : (
              <img
                src={getMediaUrl(previewMedia.url)}
                alt={previewMedia.originalName || "Gallery image"}
                className="max-h-[85vh] max-w-full rounded-xl object-contain"
              />
            )}
            <p className="mt-3 text-center text-sm text-white">
              {previewMedia.originalName || previewMedia.filename}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
        </div>
        <div className="rounded-xl bg-orange-50 p-3 text-orange-600">{icon}</div>
      </div>
    </div>
  );
}
